import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  MapPin,
  Phone,
  Mail,
  Download,
  FileText,
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
  Globe,
} from 'lucide-react';
import axios from 'axios';

// Submit contact form to the PHP endpoint when deployed on same host
const API = '/api/send_email_smtp.php';

const ContactPage = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    country: '',
    inquiryType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, inquiryType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await axios.post(API, formData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (res && res.data && res.data.ok) {
        setSubmitStatus('success');
        setSubmitError('');
        setFormData({
          name: '',
          company: '',
          email: '',
          country: '',
          inquiryType: '',
          message: '',
        });
      } else {
        console.error('Server returned error:', res && res.data);
        setSubmitStatus('error');
        setSubmitError(res && res.data && res.data.error ? String(res.data.error) : 'Server error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setSubmitError(error && error.message ? String(error.message) : 'Request failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen pt-20 relative"
      style={{
        backgroundImage: "url('/images/contact_page_BG.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay for blur effect */}
      <div
        className="absolute inset-0"
        style={{
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      ></div>

      {/* Content wrapper */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-zinc-950 ">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                {t('contact.pageTitle')}
              </h1>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="p-8 bg-zinc-900/80 border border-zinc-800 rounded-2xl self-start">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-300 mb-2 block">
                        {t('contact.formName')}
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-red-500/20"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-gray-300 mb-2 block">
                        {t('contact.formCompany')}
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-red-500/20"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-gray-300 mb-2 block">
                        {t('contact.formEmail')}
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-red-500/20"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country" className="text-gray-300 mb-2 block">
                        {t('contact.formCountry')}
                      </Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-red-500/20"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="inquiryType" className="text-gray-300 mb-2 block">
                      {t('contact.formInquiry')}
                    </Label>
                    <Select onValueChange={handleSelectChange} value={formData.inquiryType}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:ring-red-500/20">
                        <SelectValue placeholder={t('contact.selectInquiryPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="consultation" className="text-white hover:bg-zinc-700">
                          {t('contact.inquiryTypes.consultation')}
                        </SelectItem>
                        <SelectItem value="partnership" className="text-white hover:bg-zinc-700">
                          {t('contact.inquiryTypes.partnership')}
                        </SelectItem>
                        <SelectItem value="general" className="text-white hover:bg-zinc-700">
                          {t('contact.inquiryTypes.general')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-300 mb-2 block">
                      {t('contact.formMessage')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-red-500/20 resize-none"
                    />
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>{t('contact.successMessage')}</span>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                      <AlertCircle className="h-5 w-5 flex-shrink-0" />
                      <div>
                        <div>{t('contact.errorMessage')}</div>
                        {submitError && <div className="text-sm mt-1">{submitError}</div>}
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {t('contact.submitting')}
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        {t('contact.formSubmit')}
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                {/* Headquarters */}
                <div className="p-8 bg-zinc-900/80 border border-zinc-800 rounded-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">
                    {t('contact.hqTitle')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{t('contact.hqLocation')}</div>
                        <div className="text-gray-400 text-sm">{t('contact.hqAddress')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="text-gray-300">
                        {t('contact.hqPhone')
                          .split('/')
                          .map((s, i) => (
                            <div key={i} className="text-gray-300 text-sm" dir="ltr">
                              {s.trim()}
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="text-gray-300">
                        {t('contact.hqEmail')
                          .split('/')
                          .map((s, i) => (
                            <div key={i} className="text-gray-300 text-sm">
                              {s.trim()}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle East Office */}
                <div className="p-8 bg-zinc-900/80 border border-zinc-800 rounded-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">
                    {t('contact.dubaiOfficeTitle')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{t('contact.dubaiOfficeAddress')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div className="text-gray-300" dir="ltr">{t('contact.dubaiOfficePhone')}</div>
                    </div>
                  </div>
                </div>

                {/* Representative Office */}
                <div className="p-8 bg-zinc-900/80 border border-zinc-800 rounded-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">
                    {t('contact.repOfficeTitle')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{t('contact.repOfficeAddress')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="text-gray-300">
                        {t('contact.repOfficePhone')
                          .split('/')
                          .map((s, i) => (
                            <div key={i} className="text-gray-300 text-sm" dir="ltr">
                              {s.trim()}
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="text-gray-300">
                        {t('contact.repOfficeEmail')
                          .split('/')
                          .map((s, i) => (
                            <div key={i} className="text-gray-300 text-sm">
                              {s.trim()}
                            </div>
                          ))}
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Globe className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="text-gray-300">{t('contact.repOfficeWebsite')}</div>
                    </div> */}
                  </div>
                </div>

                {/* Downloads */}
                <div className="p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">
                    {t('contact.downloads')}
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="./petroservies-new-profile.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl hover:border-red-500/30 transition-all group"
                    >
                      <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                        <FileText className="h-6 w-6 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium text-sm">
                          {t('contact.downloadProfile')}
                        </div>
                      </div>
                     
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactPage;